/**
 * Quiz de recommandation / aide au choix (Benchmark 4.0 — P2, CDC §7.3).
 *
 * Un parcours guidé de quelques questions qui joue le rôle d'un conseiller de
 * boutique : on diagnostique honnêtement l'envie du visiteur (indécis ou
 * acheteur-cadeau) puis on recommande une collection AVEC justification —
 * jamais de manipulation, d'urgence factice ni de case pré-cochée (CDC §12.2).
 *
 * Un seul custom element, chargé par la section :
 *   <quiz-recommandation> — questions (radios natifs) → conseil motivé.
 *
 * Amélioration progressive : sans JavaScript, le parcours reste masqué et seul
 * le repli s'affiche (liens directs vers les collections conseillées). Avec JS,
 * on masque le repli et on révèle le parcours (cf. CLAUDE.md, aucun élément
 * parasite sans JS).
 *
 * Calcul 100 % côté client : chaque réponse porte un « mot-clé » ; chaque
 * résultat déclare les mots-clés qui y mènent. Le résultat au meilleur score
 * gagne. Aucun appel serveur.
 *
 * Mémoire : le dernier résultat est enregistré dans localStorage (clé
 * « boteh:quiz ») — même logique tolérante aux erreurs que la liste d'envies.
 */
const STORAGE_KEY = 'boteh:quiz';

/** Lit le dernier résultat mémorisé, tolérant aux données corrompues. */
function readMemory() {
  try {
    const raw = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
    if (!raw || typeof raw.key !== 'string') return null;
    return raw;
  } catch (error) {
    return null;
  }
}

/** Enregistre le dernier résultat. Dégradation silencieuse (navigation privée / quota). */
function writeMemory(data) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch (error) {
    /* on n'interrompt jamais l'utilisateur */
  }
}

/* ------------------------------------------------------------------ */
/* Provenance : « cette vente vient-elle du quiz ? »                    */
/* ------------------------------------------------------------------ */
/*
 * L'ajout depuis une carte du conseil porte déjà la propriété de ligne `_quiz`,
 * posée en Liquid. Mais on regarde presque toujours la FICHE avant d'acheter :
 * sans ce relais, ces ventes-là seraient invisibles et le quiz paraîtrait
 * inutile alors qu'il vend (faux négatif).
 *
 * Donc au clic sur une carte du conseil, on note « ce produit vient du conseil X ».
 * assets/product.js le relit sur la fiche et joint la même propriété.
 *
 * sessionStorage, PAS localStorage : le crédit ne dure que la visite en cours
 * (décision du marchand du 2026-08-22). Personne n'est crédité au quiz trois
 * jours plus tard. On ne mémorise QUE les produits cliqués depuis le conseil :
 * un achat trouvé par un autre chemin n'est jamais attribué au quiz.
 */
const SOURCE_KEY = 'boteh:quiz-src';
const SOURCE_MAX = 12;

/** Extrait le handle produit d'une URL de fiche, quelle que soit la locale. */
function productHandleFromHref(href) {
  try {
    const path = new URL(href, window.location.origin).pathname;
    const parts = path.split('/products/');
    if (parts.length < 2) return '';
    return parts[1].split('/')[0];
  } catch (error) {
    return '';
  }
}

/** Note « ce produit vient du conseil X » pour la visite en cours. */
function rememberSource(handle, label) {
  if (!handle || !label) return;
  try {
    let map = JSON.parse(sessionStorage.getItem(SOURCE_KEY) || '{}');
    if (!map || typeof map !== 'object' || Array.isArray(map)) map = {};
    // Plafond de sécurité : on ne laisse pas la mémoire de session grossir sans fin.
    const keys = Object.keys(map);
    if (!(handle in map) && keys.length >= SOURCE_MAX) delete map[keys[0]];
    map[handle] = label;
    sessionStorage.setItem(SOURCE_KEY, JSON.stringify(map));
  } catch (error) {
    /* stockage indisponible : on perd la mesure, jamais la vente */
  }
}

class QuizRecommandation extends HTMLElement {
  connectedCallback() {
    this.fallback = this.querySelector('[data-quiz-fallback]');
    this.interactive = this.querySelector('[data-quiz-interactive]');
    this.form = this.querySelector('[data-quiz-form]');
    if (!this.interactive || !this.form) return;

    this.steps = Array.from(this.querySelectorAll('[data-quiz-step]')).filter((step) =>
      step.querySelector('input[type="radio"]')
    );
    this.results = Array.from(this.querySelectorAll('[data-quiz-result]'));
    if (this.steps.length === 0 || this.results.length === 0) return; // config incomplète → on garde le repli

    this.noResult = this.querySelector('[data-quiz-noresult]');
    this.memoryNote = this.querySelector('[data-quiz-memory-note]');
    this.actions = this.querySelector('[data-quiz-actions]');
    this.progress = this.querySelector('[data-quiz-progress]');
    this.prevBtn = this.querySelector('[data-quiz-prev]');
    this.nextBtn = this.querySelector('[data-quiz-next]');
    this.submitBtn = this.querySelector('[data-quiz-submit]');
    this.retakeBtn = this.querySelector('[data-quiz-retake]');

    this.remember = this.getAttribute('data-remember') === 'true';
    this.progressTpl = this.getAttribute('data-tpl-progress') || '';
    this.current = 0;

    // Bascule amélioration progressive : on cache le repli, on révèle le parcours.
    if (this.fallback) this.fallback.setAttribute('hidden', '');
    this.interactive.removeAttribute('hidden');

    this.form.addEventListener('submit', (event) => {
      event.preventDefault();
      this.showResult();
    });
    this.form.addEventListener('change', () => this.updateNav());
    if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.go(1));
    if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.go(-1));
    if (this.retakeBtn) this.retakeBtn.addEventListener('click', () => this.restart());

    // Provenance : clic sur un lien produit d'un conseil (image, titre, « Choisir »).
    // Délégué sur l'élément entier : marche aussi pour les conseils révélés plus tard.
    this.addEventListener('click', (event) => {
      const link = event.target.closest('a[href*="/products/"]');
      if (!link) return;
      const result = link.closest('[data-quiz-result]');
      if (!result) return;
      rememberSource(
        productHandleFromHref(link.getAttribute('href')),
        result.getAttribute('data-quiz-label')
      );
    });

    // Restauration d'un conseil mémorisé, si l'option est active et le résultat existe encore.
    const saved = this.remember ? readMemory() : null;
    const savedResult =
      saved && this.results.find((el) => el.getAttribute('data-result-key') === saved.key);
    if (savedResult) {
      this.form.setAttribute('hidden', '');
      this.reveal(savedResult, false);
      if (this.memoryNote) this.memoryNote.removeAttribute('hidden');
    } else {
      this.showStep(0);
    }
  }

  /** L'étape courante a-t-elle une réponse cochée ? */
  currentAnswered() {
    const step = this.steps[this.current];
    return !!step && !!step.querySelector('input[type="radio"]:checked');
  }

  showStep(index) {
    this.current = Math.max(0, Math.min(index, this.steps.length - 1));
    this.steps.forEach((step, i) => step.toggleAttribute('hidden', i !== this.current));
    this.form.removeAttribute('hidden');
    this.hideResults();
    if (this.memoryNote) this.memoryNote.setAttribute('hidden', '');
    if (this.actions) this.actions.setAttribute('hidden', '');
    this.updateProgress();
    this.updateNav();
  }

  updateProgress() {
    if (!this.progress) return;
    this.progress.textContent = this.progressTpl
      .replace('[current]', String(this.current + 1))
      .replace('[total]', String(this.steps.length));
  }

  updateNav() {
    const isLast = this.current === this.steps.length - 1;
    const answered = this.currentAnswered();
    if (this.prevBtn) this.prevBtn.toggleAttribute('hidden', this.current === 0);
    if (this.nextBtn) {
      this.nextBtn.toggleAttribute('hidden', isLast);
      this.nextBtn.disabled = !answered;
    }
    if (this.submitBtn) {
      this.submitBtn.toggleAttribute('hidden', !isLast);
      this.submitBtn.disabled = !answered;
    }
  }

  go(direction) {
    if (direction > 0 && !this.currentAnswered()) return;
    const next = this.current + direction;
    if (next < 0 || next > this.steps.length - 1) return;
    this.showStep(next);
    const legend = this.steps[this.current].querySelector('legend');
    if (legend) {
      legend.setAttribute('tabindex', '-1');
      legend.focus();
    }
  }

  /** Rassemble les mots-clés des réponses cochées (minuscules). */
  collectKeys() {
    const keys = [];
    this.steps.forEach((step) => {
      const checked = step.querySelector('input[type="radio"]:checked');
      if (checked && checked.value) keys.push(checked.value.trim().toLowerCase());
    });
    return keys;
  }

  /** Compte combien de mots-clés du résultat figurent dans les réponses. */
  scoreResult(el, keys) {
    return (el.getAttribute('data-match-keys') || '')
      .toLowerCase()
      .split(/[\s,]+/)
      .map((key) => key.trim())
      .filter(Boolean)
      .reduce((total, key) => total + (keys.includes(key) ? 1 : 0), 0);
  }

  /** Résultat au meilleur score (égalité → premier défini) ; null si aucun ne matche. */
  pickResult(keys) {
    let best = null;
    let bestScore = 0;
    this.results.forEach((el) => {
      const score = this.scoreResult(el, keys);
      if (score > bestScore) {
        bestScore = score;
        best = el;
      }
    });
    return best;
  }

  showResult() {
    const keys = this.collectKeys();
    const winner = this.pickResult(keys);
    this.form.setAttribute('hidden', '');
    if (winner) {
      const resultKey = winner.getAttribute('data-result-key');
      // Ne mémoriser que si la clé existe, sinon readMemory ne restaurera jamais ce résultat.
      if (this.remember && resultKey) writeMemory({ key: resultKey, keys });
      this.reveal(winner, true);
    } else {
      this.hideResults();
      if (this.noResult) {
        this.noResult.removeAttribute('hidden');
        this.focusEl(this.noResult);
      }
      if (this.actions) this.actions.removeAttribute('hidden');
    }
  }

  reveal(el, focus) {
    this.hideResults();
    el.removeAttribute('hidden');
    if (this.actions) this.actions.removeAttribute('hidden');
    if (focus) this.focusEl(el.querySelector('[data-quiz-result-title]') || el);
  }

  focusEl(el) {
    if (!el) return;
    if (!el.hasAttribute('tabindex')) el.setAttribute('tabindex', '-1');
    el.focus();
  }

  hideResults() {
    this.results.forEach((el) => el.setAttribute('hidden', ''));
    if (this.noResult) this.noResult.setAttribute('hidden', '');
  }

  restart() {
    this.form
      .querySelectorAll('input[type="radio"]:checked')
      .forEach((radio) => {
        radio.checked = false;
      });
    this.showStep(0);
    const legend = this.steps[0].querySelector('legend');
    if (legend) {
      legend.setAttribute('tabindex', '-1');
      legend.focus();
    }
  }
}

if (!customElements.get('quiz-recommandation')) {
  customElements.define('quiz-recommandation', QuizRecommandation);
}
